import { useQuery } from "@tanstack/react-query";
import { apiFetch, type Case } from "../api.js";
import { useAuth } from "../auth.js";

export type Opinion = {
  id: string;
  caseId: string;
  enrolmentNo: string;
  signedAt: string;
  registryDistrict: string | null;
  landSystem: "deed" | "title" | null;
  periodSearched: string;
  conclusion: string;
};

/**
 * THE ATTORNEY'S OPINION, SHOWN AS THEIRS.
 *
 * D2 (answered 2026-08-16): the deliverable is the attorney's own signed
 * opinion and this platform is the courier. That decides how this renders.
 *
 * THE CONCLUSION IS QUOTED, NOT SUMMARISED. It appears in the attorney's exact
 * words, whole, with no heading of ours above it that characterises it, no
 * badge, no colour coding, no extracted "outcome". COMPLIANCE Constraint 3
 * forbids the platform to generate or template an ownership conclusion, and a
 * green tick reading "no encumbrances" over someone else's paragraph is exactly
 * that — our summary of their legal opinion, which is the one thing we may
 * never write.
 *
 * SO THERE IS NO VERDICT COMPONENT HERE and there must never be one. If a
 * future reader thinks this screen would be clearer with a status chip
 * summarising the finding: that is the platform practising law.
 *
 * WHO SIGNED IT is the enrolment number, not a name. Whether the client may be
 * shown the NAME is Q3, still with the reviewing attorney; and the schema holds
 * no verified name to show — users.full_name is self-supplied, nullable and
 * erasable, while the enrolment number is what an admin checked against a named
 * source. When Q3 is answered this is where the name goes.
 */
export function CaseOpinion({ caseRow }: { caseRow: Case }) {
  const { getToken } = useAuth();

  const { data, isPending } = useQuery({
    queryKey: ["opinion", caseRow.id],
    queryFn: async () =>
      apiFetch<{ opinion: Opinion | null }>(`/cases/${caseRow.id}/opinion`, {
        token: await getToken(),
      }),
    // Only ask once the case has reached a state where one can exist. Asking
    // earlier would be a request per case-card render for a row that is null
    // for most of a case's life.
    enabled: ["OPINION_READY", "PACKAGED", "DELIVERED", "CLOSED"].includes(caseRow.status),
  });

  if (isPending || !data?.opinion) return null;
  const opinion = data.opinion;

  return (
    <section className="opinion">
      <p className="eyebrow">The attorney&rsquo;s opinion</p>

      {/*
        Their words, whole and unedited. `white-space: pre-wrap` so the
        paragraphing they chose survives — reflowing someone's legal opinion is
        a small edit to a document that is not ours to edit.
      */}
      <blockquote className="opinion-text">{opinion.conclusion}</blockquote>

      <dl className="opinion-provenance">
        <dt>Signed by</dt>
        <dd>
          Attorney-at-Law, enrolment no. <code>{opinion.enrolmentNo}</code>
          <br />
          <small className="muted">
            The enrolment number an administrator checked against a named source before this
            attorney could be given any work.
          </small>
        </dd>

        <dt>Period searched</dt>
        <dd>{opinion.periodSearched}</dd>

        {opinion.registryDistrict ? (
          <>
            <dt>Land registry</dt>
            <dd>{opinion.registryDistrict}</dd>
          </>
        ) : null}

        <dt>Signed on</dt>
        <dd>{new Date(opinion.signedAt).toLocaleDateString()}</dd>
      </dl>

      <p className="muted">
        <small>
          This opinion is the attorney&rsquo;s own. TitleVerify carried the request to them and
          holds their answer for you — we did not write it, we do not hold a view on it, and we
          cannot tell you what it means for your purchase. That is a question for the attorney
          who signed it.
        </small>
      </p>
    </section>
  );
}
